import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { notifyAvatarChanged } from '@/common/avatarEvents';

type CropData = { imageOriginalUrl: string; imageCroppedUrl: string; crop: { x: number; y: number; width: number; height: number } };

export function useAvatarSave(userId: string | undefined, refreshTrigger: number) {
  const services = useServices();
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>(undefined);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    services.uiImages.getUIImages({ userId, imageCategory: 'Avatar' }).then(result => {
      if (cancelled || !result.success) return;
      setCurrentImageUrl(result.data?.[0]?.imageOriginalUrl || undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, services.uiImages, refreshTrigger, version]);

  const existingAvatars = async () => {
    const result = await services.uiImages.getUIImages({ userId: userId!, imageCategory: 'Avatar' });
    if (!result.success) throw new Error("Couldn't read your current photo");
    return result.data ?? [];
  };

  const run = async (write: () => Promise<void>, success: string, failure: string) => {
    if (!userId) {
      toast.error('No user found');
      return;
    }
    try {
      await write();
      setVersion(n => n + 1);
      notifyAvatarChanged();
      toast.success(success);
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error(failure);
    }
  };

  const saveImage = async (fields: { imageOriginalUrl: string; imageCroppedUrl: string; imageMetadata?: Record<string, any> }) => {
    const [existing] = await existingAvatars();
    const result = existing
      ? await services.uiImages.updateUIImage(existing.imageId, fields)
      : await services.uiImages.createUIImage({ imageCategory: 'Avatar', userId: userId!, ...fields });
    if (!result.success) throw new Error(result.error);
  };

  const removeImage = async () => {
    for (const image of await existingAvatars()) {
      const result = await services.uiImages.deleteUIImage(image.imageId);
      if (!result.success) throw new Error(result.error);
    }
  };

  const onCropApplied = (data: CropData) =>
    run(
      () => saveImage({ imageOriginalUrl: data.imageOriginalUrl, imageCroppedUrl: data.imageCroppedUrl, imageMetadata: { crop: data.crop } }),
      'Profile photo updated',
      "Couldn't update your profile photo. Please try again.",
    );

  const onImageSelect = (imageUrl: string) =>
    imageUrl
      ? run(() => saveImage({ imageOriginalUrl: imageUrl, imageCroppedUrl: imageUrl }), 'Profile photo updated', "Couldn't update your profile photo. Please try again.")
      : run(removeImage, 'Profile photo removed', "Couldn't remove your profile photo. Please try again.");

  return { currentImageUrl, onCropApplied, onImageSelect };
}
